import { useCallback, useEffect, useRef, useState } from "react";

import { isComparableBuild, type BuildInfo } from "@/lib/build-info";
import { scheduleWhileVisible } from "@/lib/schedule-while-visible";
import { fetchPublishedBuild, isStale } from "./check-build-manifest";

/** How often an open tab re-checks the published manifest. */
const CHECK_INTERVAL_MS = 60 * 1000;

/** How long a manual "you are up to date" confirmation stays on screen. */
const CONFIRMATION_MS = 2500;

export type BuildStaleness = {
  /** A newer build has been published than the one running. */
  stale: boolean;
  /** A manually requested check is in flight. */
  checking: boolean;
  /** A manual check just confirmed this build is the newest. */
  confirmed: boolean;
  /** Run a check right now, independent of the background triggers. */
  check: () => void;
};

/**
 * Tracks whether the running build has been superseded by a newer deployment.
 *
 * Checks run on mount and then on the shared foreground / reconnect / interval
 * triggers. A build with no identity (a local build) is never checked — there
 * is no published manifest to compare it against.
 *
 * Deliberately independent of the service worker: see [[build-stamp-chip.spec]]
 * for why the update worker's silence cannot be read as "you are current".
 */
export function useBuildStaleness(
  running: BuildInfo,
  intervalMs: number = CHECK_INTERVAL_MS,
): BuildStaleness {
  const [stale, setStale] = useState(false);
  const [checking, setChecking] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Guards state updates against a fetch that resolves after unmount.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Deliberately free of *synchronous* state writes: this runs from an effect,
  // where setting state before the first await would trigger a cascading
  // render. The manual path raises `checking` itself, in the event handler.
  const runCheck = useCallback(
    async (manual: boolean) => {
      if (!isComparableBuild(running)) return;

      const published = await fetchPublishedBuild();
      if (!mounted.current) return;
      if (manual) setChecking(false);

      // A failed check is silent — hold the last known state rather than
      // asserting a freshness we did not actually verify.
      if (published === null) return;

      const nextStale = isStale(running, published);
      setStale(nextStale);
      if (manual && !nextStale) setConfirmed(true);
    },
    [running],
  );

  // The first check is delegated to the scheduler rather than fired here, so it
  // inherits the same hidden/offline guard as every later one.
  useEffect(() => {
    if (!isComparableBuild(running)) return;
    return scheduleWhileVisible(() => void runCheck(false), intervalMs, {
      immediate: true,
    });
  }, [running, runCheck, intervalMs]);

  // Let the confirmation fade so the chip settles back to naming the build.
  useEffect(() => {
    if (!confirmed) return;
    const id = window.setTimeout(() => {
      if (mounted.current) setConfirmed(false);
    }, CONFIRMATION_MS);
    return () => window.clearTimeout(id);
  }, [confirmed]);

  const check = useCallback(() => {
    // A build with no identity has no manifest to compare against, so the busy
    // state would never be cleared — don't raise it in the first place.
    if (!isComparableBuild(running)) return;
    setChecking(true);
    void runCheck(true);
  }, [runCheck, running]);

  return { stale, checking, confirmed, check };
}
